import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Plus, Trash2, ChevronRight, ChevronDown, AlertCircle, Download, FileJson, Send, RefreshCw } from 'lucide-react';
import legacyUapiService from '../../services/LegacyUapiService';
import Button from './Button';
import Spinner from './Spinner';
import ConfirmModal from './ConfirmModal';
import UapiRoutePayload from './UapiRoutePayload';
import Tabs from './Tabs';
import '../PaymentPorterProcessorPage.css';   // .ppp-detail-btn — the site's list-row action button
import UapiOperationPayload from './UapiOperationPayload';
import UapiOperationImport from './UapiOperationImport';
import '../UniversalApiPage.css';

// THE Universal API surface — the route tree and the operations hanging off each route.
//
// Extracted so the DEFINITION has one implementation and two homes:
//   Admin > Platform > Universal API   the surface itself, a platform-wide artefact
//   Legacy > Universal API             the same surface, in the context of a property whose
//                                      authentication is being provisioned against it
//
// Those are different jobs — one edits what the API IS, the other issues credentials for it — and the
// only reason they shared a screen is that the screen was written before the distinction mattered. A
// route added here changes the API for every property, which is precisely the fact a per-hotel screen
// obscures.
//
// The tree is built from parent_id, NOT by splitting path strings. The integration-builder sandbox
// originally inferred it from text and had to add parent_id later (its V24): with inference, renaming a
// segment silently orphans every descendant, because their stored paths stop matching the parent they
// are drawn under.
//
// Props:
//   onTestOperation — adds a Test button to each operation row, called with that operation. The
//                     provisioning screen opens its request modal from it: the tree already lists every
//                     operation with its module and description, and a second list of the same 201 rows
//                     to choose from would be a second thing to keep in step.
//
//   readOnly  — hide EVERY control that writes: both add rows and both delete buttons. The provisioning
//               screen shows the surface for reference; changing it there would be a platform-wide edit
//               made from a property page. It guarded the add rows only until 2026-08-28, so a screen
//               that called itself read-only still deleted a route and everything under it.


const METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];

// How a profile target is named on screen. The database calls a chain's table `accounts`; nobody else
// does, and a column reading "Account: Hilton" would be the one word the rest of the portal avoids.
const SCOPE_LABEL = { chain: 'Chain', hotel: 'Hotel', credential: 'Credential' };

// parent_id -> children. Any route whose parent is missing is treated as a root so it stays visible:
// a row you cannot see is a row you cannot fix.
function buildTree(routes) {
    const byId = new Map(routes.map((r) => [String(r.id), r]));
    const children = new Map();
    const roots = [];
    routes.forEach((r) => {
        const pid = r.parent_id == null ? null : String(r.parent_id);
        if (pid && byId.has(pid)) {
            if (!children.has(pid)) children.set(pid, []);
            children.get(pid).push(r);
        } else {
            roots.push(r);
        }
    });
    return { roots, children };
}

export default function UapiSurface({ readOnly = false, onTestOperation = null, credentialId = null, hotelId = null }) {
    const [routes, setRoutes] = useState([]);
    const [operations, setOperations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expanded, setExpanded] = useState({});
    const [selectedRouteId, setSelectedRouteId] = useState(null);
    // Which side of the selected route is showing: the operations declared on it, or the payloads
    // imported from Oracle's spec for it.
    const [routeTab, setRouteTab] = useState('operations');
    // The operation whose body is open, and the spec preview for importing operations. Both belong to
    // the selected route, so both clear when it changes.
    const [payloadOp, setPayloadOp] = useState(null);
    const [opPreview, setOpPreview] = useState(null);
    const [opImporting, setOpImporting] = useState(false);

    // WHAT EACH CALL WILL BE REDACTED TO, before anyone makes it.
    //
    // The surface said what a property can reach and nothing about what it will be shown of the answer,
    // which is the other half of the same question and is knowable in advance. Resolved on the server
    // with the gateway's own functions, so this cannot promise a redaction the request then does
    // differently. Keyed by operation id.
    const [redactions, setRedactions] = useState({});

    const [newRoutePath, setNewRoutePath] = useState('');
    const [newRouteParent, setNewRouteParent] = useState('');
    const [newOp, setNewOp] = useState({ method: 'GET', path: '', description: '' });
    const [saving, setSaving] = useState(false);
    const [confirm, setConfirm] = useState(null);

    // ── RE-IMPORT EVERY MODULE FROM ITS SPEC ─────────────────────────────────
    //
    // Payloads are copies of Oracle's published documents, taken when somebody imported them. When the
    // importer's rules change (HIL-97: a write with its own body gets its own document) or Oracle revises a
    // spec, every module has to be imported again to pick it up — and doing that one Payload tab at a time,
    // on every environment, is how it does not get done.
    //
    // WHAT WAS IMPORTED, NOT THE WHOLE SPEC. Each module re-imports exactly the operation paths it holds
    // now, so this refreshes what is there without adding the dozens of documents nobody chose (rsv.json
    // publishes 155) or pruning any. One request per module, from here, in turn: all twelve in one call
    // would run past the gateway's 29 seconds.
    const [reimport, setReimport] = useState(null);   // { running, done, total, current, results: [] }
    const [confirmReimport, setConfirmReimport] = useState(false);
    const modulesWithSpecs = useMemo(
        () => routes.filter(r => r.parent_id == null && r.spec_file && Number(r.payload_count) > 0),
        [routes]);
    const runReimport = async () => {
        setConfirmReimport(false);
        const list = modulesWithSpecs;
        const results = [];
        setReimport({ running: true, done: 0, total: list.length, current: null, results });
        for (const [i, m] of list.entries()) {
            setReimport(r => ({ ...r, current: m.path }));
            try {
                const held = await legacyUapiService.listRoutePayloads(m.id);
                const paths = [...new Set(((held.data || held).payloads || []).map(p => p.operation_path).filter(Boolean))];
                const res = await legacyUapiService.importRoutePayloads(m.id, null, paths);
                const d = res.data || res;
                results.push({ path: m.path, ok: true, text: `${d.imported} document(s) from ${d.specFile} ${d.specVersion || ''}`.trim() });
            } catch (e) {
                results.push({ path: m.path, ok: false,
                    text: e.response?.data?.error?.message || e.response?.data?.message || e.message || 'failed' });
            }
            setReimport(r => ({ ...r, done: i + 1, results: [...results] }));
        }
        setReimport(r => ({ ...r, running: false, current: null }));
        load();
    };

    const load = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const [routesRes, opsRes] = await Promise.all([
                legacyUapiService.listRoutes(),
                legacyUapiService.listAllOperations(),
            ]);
            const list = routesRes.data?.routes || [];
            setRoutes(list);
            setOperations(opsRes.data?.operations || []);
            // Expand everything on load: the tree is small and a collapsed tree hides the thing you
            // came to look at.
            setExpanded(Object.fromEntries(list.map((r) => [String(r.id), true])));
        } catch (e) {
            setError(e.response?.data?.message || e.message || 'Failed to load the API surface');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const act = async (fn, onDone) => {
        try {
            setSaving(true);
            setError(null);
            await fn();
            await load();
            if (onDone) onDone();
        } catch (e) {
            // 409 is a duplicate path or a duplicate method+path — the two mistakes actually made here,
            // and the lambda words them properly, so the server's message is shown rather than a generic.
            setError(e.response?.data?.message || e.message || 'Request failed');
        } finally {
            setSaving(false);
        }
    };

    const addRoute = () => act(
        () => legacyUapiService.createRoute({
            path: newRoutePath.trim(),
            parent_id: newRouteParent || null,
        }),
        () => { setNewRoutePath(''); setNewRouteParent(''); },
    );

    const addOperation = () => act(
        () => legacyUapiService.createOperation(selectedRouteId, {
            method: newOp.method,
            path: newOp.path.trim(),
            description: newOp.description.trim() || null,
        }),
        () => setNewOp({ method: 'GET', path: '', description: '' }),
    );

    const { roots, children } = buildTree(routes);
    const selectedRoute = routes.find((r) => String(r.id) === String(selectedRouteId)) || null;

    // Fetch what the module's spec publishes. Nothing is written until the picker says which.
    const openOperationImport = async () => {
        if (!selectedRoute) return;
        setOpImporting(true);
        setError(null);
        try {
            const res = await legacyUapiService.previewSpecOperations(selectedRoute.id);
            const d = res.data || res;
            setOpPreview({ ...d, routeId: selectedRoute.id });
        } catch (e) {
            setError(e.response?.data?.error?.message || e.response?.data?.message || e.message
                || 'Could not read that spec');
        } finally {
            setOpImporting(false);
        }
    };
    const opsForRoute = (routeId) => operations.filter((o) => String(o.route_id) === String(routeId));



    // Every route at or beneath one, deepest-first order irrelevant — used to answer "what can be called
    // under /act", which is the question someone selecting /act is actually asking.
    const routeAndDescendants = (routeId) => {
        const out = [];
        const walk = (id) => {
            const route = routes.find((r) => String(r.id) === String(id));
            if (route) out.push(route);
            (children.get(String(id)) || []).forEach((c) => walk(c.id));
        };
        walk(routeId);
        return out;
    };

    // Operations of a route AND of everything under it.
    //
    // WHY NOT JUST THE ROUTE'S OWN. The tree is versioned — /act holds nothing and /act/v1 holds fifteen
    // — so selecting the module a person came looking for showed "No operations on this route yet" next
    // to a child badged 15. That is true and useless: the operations exist, one level down, and the
    // screen was reporting the shape of the tree rather than answering the question. Each row names the
    // route it belongs to when that is not the selected one, so nothing is lost by including them.
    const opsForRouteTree = (routeId) => {
        const branch = routeAndDescendants(routeId);
        const byId = new Map(branch.map((r) => [String(r.id), r]));
        return operations
            .filter((o) => byId.has(String(o.route_id)))
            .map((o) => ({ ...o, _route: byId.get(String(o.route_id)) }))
            .sort((a, b) => (a._route.path === b._route.path
                ? String(a.path).localeCompare(String(b.path))
                : String(a._route.path).localeCompare(String(b._route.path))));
    };

    // Fetched for the operations ON SCREEN, not for the whole surface: resolving a redaction is several
    // queries per operation, and the answer is decoration on a table the reader is already looking at.
    // Re-runs when the route, the credential or the property changes, because all three change the
    // answer.
    const shownOpIds = useMemo(
        () => (selectedRouteId ? opsForRouteTree(selectedRouteId).map(o => o.id) : []),
        [selectedRouteId, operations]);

    useEffect(() => {
        let cancelled = false;
        if (!shownOpIds.length) { setRedactions({}); return undefined; }
        legacyUapiService.getOperationRedactions({ credentialId, hotelId, operationIds: shownOpIds })
            .then((res) => {
                if (cancelled) return;
                const map = {};
                for (const r of (res.data?.operations || [])) map[String(r.operationId)] = r;
                setRedactions(map);
            })
            // Silent: this annotates a table that is useful without it, and an error banner for a
            // decoration would push the thing the reader came for off the screen.
            .catch(() => { if (!cancelled) setRedactions({}); });
        return () => { cancelled = true; };
    }, [shownOpIds, credentialId, hotelId]);


    // `systems` arrives pipe-separated from GROUP_CONCAT — a separator that cannot occur in a system
    // name, unlike the comma several of them contain.
    const systemsOf = (route) => String(route.systems || '').split('|').filter(Boolean);

    // What a row is labelled with. A child stores its FULL path (/act/v1, parent /act), so printing that
    // under its parent shows "/act" twice — once as the parent row, once as the head of every child
    // beneath it, which reads as a duplicate rather than as a hierarchy. The row shows the segment the
    // child adds; the full path is on the panel header and on every operation.
    const routeLabel = (route) => {
        if (route.parent_id == null) return route.path;
        const parent = routes.find((r) => String(r.id) === String(route.parent_id));
        if (!parent || !String(route.path).startsWith(parent.path)) return route.path;
        return String(route.path).slice(parent.path.length) || route.path;
    };

    const renderRoute = (route, depth) => {
        const id = String(route.id);
        const kids = children.get(id) || [];
        const isOpen = expanded[id];
        const opCount = opsForRoute(id).length;
        return (
            <React.Fragment key={id}>
                <div
                    className={`lu-route ${String(selectedRouteId) === id ? 'lu-route-selected' : ''}`}
                    style={{ paddingLeft: `${depth * 18 + 10}px` }}
                    onClick={() => {
                        setSelectedRouteId(route.id);
                        // The Payload tab does not exist on a child route; staying on it would show an
                        // empty panel with no tab selected.
                        if (route.parent_id != null) setRouteTab('operations');
                    }}
                >
                    <button
                        type="button"
                        className="lu-twisty"
                        onClick={(e) => { e.stopPropagation(); setExpanded((x) => ({ ...x, [id]: !x[id] })); }}
                        aria-label={isOpen ? 'Collapse' : 'Expand'}
                    >
                        {kids.length > 0
                            ? (isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />)
                            : <span className="lu-twisty-spacer" />}
                    </button>
                    {/* THE PATH, AND THE SYSTEMS AS A TITLE RATHER THAN AS CHIPS.
                        
                        Each root module used to carry a chip per marketplace that serves it — "Opera
                        Cloud via OHIP", "OPERA OXI", "HCONNECT Universal API MQ". On a narrow tree those
                        chips are wider than the paths they sit beside, so they pushed the path out of
                        the row: a module served by two systems showed two chips and NO path at all, and
                        /mq rendered as a bare "/" with a chip after it. The tree exists to show the
                        shape of the API, and the one thing it stopped showing was the shape.
                        
                        The information is not lost — it is on the row's tooltip, and the panel to the
                        right names the backend for whatever is selected, which is where somebody asking
                        "who serves this" is actually looking. */}
                    <span
                        className="lu-route-path"
                        title={!route.parent_id && systemsOf(route).length
                            ? `${route.path} — served by ${systemsOf(route).join(', ')}`
                            : route.path}
                    >
                        {routeLabel(route)}
                    </span>
                    {opCount > 0 && <span className="lu-count">{opCount}</span>}
                    {!readOnly && (
                    <button
                        type="button"
                        className="lu-icon-btn"
                        title="Delete this route, everything under it and its operations"
                        onClick={(e) => {
                            e.stopPropagation();
                            setConfirm({
                                title: 'Delete route',
                                message: `Delete ${route.path}? Any routes beneath it and all their operations are deleted too.`,
                                onConfirm: () => act(() => legacyUapiService.deleteRoute(route.id),
                                    () => { if (String(selectedRouteId) === id) setSelectedRouteId(null); }),
                            });
                        }}
                    >
                        <Trash2 size={13} />
                    </button>
                    )}
                </div>
                {isOpen && kids.map((k) => renderRoute(k, depth + 1))}
            </React.Fragment>
        );
    };


    if (loading) return <div className="loading-section"><Spinner size="md" /><p>Loading API surface...</p></div>;

    return (
        <>
            {error && (
                <div className="error-alert">
                    <div className="error-alert-content">
                        <AlertCircle className="error-alert-icon" />
                        <p className="error-alert-message">{error}</p>
                    </div>
                </div>
            )}

                <div className="lu-layout">
                    {/* Routes */}
                    <div className="lu-panel">
                        <div className="lu-panel-header">
                            <h2>Routes</h2>
                            {!readOnly && modulesWithSpecs.length > 0 && (
                                <button type="button" className="ppp-detail-btn"
                                    onClick={() => setConfirmReimport(true)}
                                    disabled={reimport && reimport.running}
                                    title="Import every module's payloads again from Oracle's published spec">
                                    <RefreshCw size={13} />
                                    {reimport && reimport.running
                                        ? ` Re-importing ${reimport.done + 1}/${reimport.total}${reimport.current ? ` (${reimport.current})` : ''}…`
                                        : ' Re-import specs'}
                                </button>
                            )}
                        </div>
                        {reimport && !reimport.running && reimport.results.length > 0 && (
                            <div className="lu-reimport-results">
                                {reimport.results.map(r => (
                                    <div key={r.path} className={r.ok ? 'lu-reimport-ok' : 'lu-reimport-bad'}>
                                        <code>{r.path}</code> {r.text}
                                    </div>
                                ))}
                            </div>
                        )}

                        {!readOnly && (
                        <div className="lu-add-row">
                            <input
                                className="form-input"
                                placeholder="/hsk/v1"
                                value={newRoutePath}
                                onChange={(e) => setNewRoutePath(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter' && newRoutePath.trim()) addRoute(); }}
                            />
                            <select
                                className="form-input lu-parent-select"
                                value={newRouteParent}
                                onChange={(e) => setNewRouteParent(e.target.value)}
                            >
                                <option value="">No parent (top level)</option>
                                {routes.map((r) => <option key={r.id} value={r.id}>{r.path}</option>)}
                            </select>
                            <Button variant="primary" onClick={addRoute} disabled={saving || !newRoutePath.trim()}>
                                <Plus className="icon-sm" />Add
                            </Button>
                        </div>
                        )}

                        <div className="lu-tree">
                            {roots.length === 0
                                ? <p className="lu-empty">No routes yet. Add one above.</p>
                                : roots.map((r) => renderRoute(r, 0))}
                        </div>
                    </div>

                    {/* Operations for the selected route */}
                    <div className="lu-panel">
                        <div className="lu-panel-header">
                            <h2>{selectedRoute ? selectedRoute.path : 'Route'}</h2>
                            {selectedRoute && (
                                /* Operations: what the route declares. Payload: what travels over it, as
                                   JSON Schema imported from Oracle's spec for the module. */
                                <Tabs
                                    size="sm"
                                    className="lu-panel-tabs"
                                    active={routeTab}
                                    onChange={setRouteTab}
                                    tabs={[
                                        {
                                            key: 'operations',
                                            label: 'Operations',
                                            icon: (
                                                <svg viewBox="0 0 20 20" fill="none">
                                                    <path d="M7 5 3 10l4 5M13 5l4 5-4 5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                                                </svg>
                                            ),
                                        },
                                        {
                                            key: 'payload',
                                            label: 'Payload',
                                            // A payload belongs to a MODULE. Oracle publishes one spec
                                            // per module and the message is the module's, so the routes
                                            // beneath it have none of their own.
                                            hidden: selectedRoute.parent_id != null,
                                            icon: (
                                                <svg viewBox="0 0 20 20" fill="none">
                                                    <path d="M4 5.5A1.5 1.5 0 0 1 5.5 4H12l4 4v6.5a1.5 1.5 0 0 1-1.5 1.5h-9A1.5 1.5 0 0 1 4 14.5v-9z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/>
                                                    <path d="M12 4v4h4M7 11h6M7 13.5h4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                                                </svg>
                                            ),
                                        },
                                    ]}
                                />
                            )}
                        </div>

                        {!selectedRoute ? (
                            <p className="lu-empty">Select a route to see its operations and payloads.</p>
                        ) : routeTab === 'payload' ? (
                            <UapiRoutePayload route={selectedRoute} readOnly={readOnly} />
                        ) : (
                            <>
                                {!readOnly && (children.get(String(selectedRoute.id)) || []).length > 0 && (
                                    <p className="lu-add-target">
                                        Operations below include everything under {selectedRoute.path}. A new
                                        one is added to {selectedRoute.path} itself — select the child route to
                                        add it there instead.
                                    </p>
                                )}
                                {!readOnly && (
                                <div className="lu-add-row">
                                    <select
                                        className="form-input lu-method-select"
                                        value={newOp.method}
                                        onChange={(e) => setNewOp({ ...newOp, method: e.target.value })}
                                    >
                                        {METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
                                    </select>
                                    <input
                                        className="form-input"
                                        placeholder="/rooms/{roomId}"
                                        value={newOp.path}
                                        onChange={(e) => setNewOp({ ...newOp, path: e.target.value })}
                                        onKeyDown={(e) => { if (e.key === 'Enter' && newOp.path.trim()) addOperation(); }}
                                    />
                                    <input
                                        className="form-input"
                                        placeholder="Description (optional)"
                                        value={newOp.description}
                                        onChange={(e) => setNewOp({ ...newOp, description: e.target.value })}
                                    />
                                    <Button variant="primary" onClick={addOperation} disabled={saving || !newOp.path.trim()}>
                                        <Plus className="icon-sm" />Add
                                    </Button>
                                    {/* Typing 23 method+path pairs for act.json — or 200 for rsv.json —
                                        is how a tree comes to describe calls the API does not have. The
                                        spec already states them. */}
                                    <Button
                                        variant="secondary"
                                        onClick={openOperationImport}
                                        disabled={saving || opImporting}
                                        title="Create operations from Oracle's published spec for this module"
                                    >
                                        <Download className="icon-sm" />{opImporting ? 'Reading…' : 'Import from spec'}
                                    </Button>
                                </div>
                                )}

                                <table className="records-table">
                                    <thead>
                                        <tr>
                                            <th style={{ width: '90px' }}>Method</th>
                                            <th>Path</th>
                                            <th>Description</th>
                                            <th style={{ width: '190px' }}>Redaction</th>
                                            <th className="action-header" />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {opsForRouteTree(selectedRoute.id).length === 0 ? (
                                            <tr>
                                                <td colSpan={5} className="empty-cell">
                                                    <p className="empty-message">
                                                        No operations on {selectedRoute.path} or anything under it yet
                                                    </p>
                                                </td>
                                            </tr>
                                        ) : opsForRouteTree(selectedRoute.id).map((op) => (
                                            <tr key={op.id}>
                                                <td><span className={`lu-method lu-method-${op.method.toLowerCase()}`}>{op.method}</span></td>
                                                <td className="lu-mono">
                                                    {/* Named only when it belongs to a child: on the
                                                        route's own operations the prefix would be noise
                                                        repeated on every row. */}
                                                    {String(op._route.id) !== String(selectedRoute.id) && (
                                                        <span className="lu-op-route">{op._route.path}</span>
                                                    )}
                                                    {op.path}
                                                </td>
                                                <td>{op.description || <span className="lu-muted">—</span>}</td>
                                                {/* THE ANSWER, OR WHY THERE ISN'T ONE. Four distinct
                                                    states, because they mean different things: no
                                                    document mapped to this call, a set that will strike
                                                    nothing, a set that will, and a misconfiguration the
                                                    gateway would turn into a 500 at request time. */}
                                                <td className="lu-op-redaction">
                                                    {(() => {
                                                        const r = redactions[String(op.id)];
                                                        if (!r) return <span className="lu-muted">—</span>;
                                                        if (r.error) return (
                                                            <span className="lu-redaction-bad" title={r.error}>misconfigured</span>
                                                        );
                                                        if (!r.payload) return (
                                                            <span className="lu-muted" title="No payload is mapped to this call, so nothing can be redacted">
                                                                no payload
                                                            </span>
                                                        );
                                                        // WHERE IT COMES FROM, in the reader's terms and
                                                        // by NAME. Exactly ONE profile can apply to a
                                                        // credential, so there is one answer here and it
                                                        // is worth stating precisely: the profile is
                                                        // changed for every property following it, and
                                                        // the useful fact is WHICH chain or hotel carries
                                                        // it. "From a hotel" is not an answer when the
                                                        // chain holds two hundred.
                                                        const f = r.set && r.set.from;
                                                        const inherited = f && f.kind && f.kind !== 'grant'
                                                            ? `${SCOPE_LABEL[f.kind] || f.kind}: ${f.name || 'unnamed'}`
                                                            : null;
                                                        const source = !r.set ? null
                                                            : inherited
                                                                ? `${inherited}, via the access profile “${f.profile}”`
                                                                : f
                                                                    ? 'a grant on this call'
                                                                    : `the ${r.set.matched} it is addressed to`;
                                                        if (!r.set || !r.set.ruleCount) return (
                                                            <span className="lu-muted" title={r.set
                                                                ? `Set "${r.set.name}", from ${source}, carries no rules for this document`
                                                                : 'Nothing is redacted for this caller'}>
                                                                nothing redacted
                                                            </span>
                                                        );
                                                        return (
                                                            <span
                                                                className="lu-redaction-set"
                                                                title={`From ${source}`
                                                                    + `\n${r.set.ruleCount} rule(s)`
                                                                    + (r.paths?.length ? `\n${r.paths.join('\n')}` : '')}
                                                            >
                                                                {r.set.name}
                                                                <em>{r.set.ruleCount}</em>
                                                                {/* Named, not only in the tooltip: an
                                                                    inherited redaction is the one a
                                                                    reader is most likely to go looking
                                                                    for and least likely to hover over,
                                                                    and the name is what tells them where
                                                                    to go and change it. */}
                                                                {inherited && (
                                                                    <span className="lu-redaction-from">
                                                                        inherited from {inherited}
                                                                    </span>
                                                                )}
                                                            </span>
                                                        );
                                                    })()}
                                                </td>
                                                <td className="lu-row-actions">
                                                    {/* Labelled, not a bare glyph: "Payload" says what
                                                        opens, where an icon needs a hover to find out.
                                                        This call's body, not the module's message. */}
                                                    {/* Sends a real request as this property's consumer.
                                                        A named button, not a clickable row: the row also
                                                        holds Payload and Delete, and a row that both
                                                        opens a dialog and contains other controls makes
                                                        every click a guess. */}
                                                    {onTestOperation && (
                                                        <button
                                                            type="button"
                                                            className="ppp-detail-btn ppp-detail-btn-primary"
                                                            title={`Send ${op.method} ${op._route.path}${op.path}`}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                onTestOperation({
                                                                    id: op.id,
                                                                    method: op.method,
                                                                    route_path: op._route.path,
                                                                    path: op.path,
                                                                    description: op.description,
                                                                });
                                                            }}
                                                        >
                                                            <Send size={12} /> Test
                                                        </button>
                                                    )}
                                                    <button
                                                        type="button"
                                                        className="ppp-detail-btn"
                                                        title={`Payload for ${op.method} ${op._route.path}${op.path}`}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setPayloadOp({ ...op, _routePath: op._route.path });
                                                        }}
                                                    >
                                                        <FileJson size={12} />
                                                        Payload
                                                    </button>
                                                    {!readOnly && (
                                                        <button
                                                            type="button"
                                                            className="lu-icon-btn lu-icon-btn-danger"
                                                            title="Delete operation"
                                                            onClick={(e) => { e.stopPropagation(); setConfirm({
                                                                title: 'Delete operation',
                                                                message: `Delete ${op.method} ${op.path}?`,
                                                                onConfirm: () => act(() => legacyUapiService.deleteOperation(op.id)),
                                                            }); }}
                                                        >
                                                            <Trash2 size={14} />
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </>
                        )}
                    </div>
                </div>

            {payloadOp && (
                <UapiOperationPayload
                    operation={payloadOp}
                    routePath={payloadOp._routePath || (selectedRoute && selectedRoute.path) || ''}
                    onClose={() => setPayloadOp(null)}
                />
            )}

            {opPreview && (
                <UapiOperationImport
                    preview={opPreview}
                    routePath={selectedRoute ? selectedRoute.path : ''}
                    onCancel={() => setOpPreview(null)}
                    onImported={(d) => {
                        setOpPreview(null);
                        // Reload so the new rows appear with the ids the payload binding used.
                        load();
                    }}
                />
            )}

            {confirmReimport && (
                <ConfirmModal
                    title="Re-import every module's payloads?"
                    message={`Each of the ${modulesWithSpecs.length} modules with a spec file imports the payloads `
                        + 'it already holds again, from Oracle\'s published spec: documents are replaced with the '
                        + 'current ones, and a write with its own body gets its own document. Nothing is added '
                        + 'that was not imported before, and nothing is removed.'}
                    confirmLabel="Re-import"
                    onConfirm={runReimport}
                    onCancel={() => setConfirmReimport(false)}
                />
            )}

            {confirm && (
                <ConfirmModal
                    title={confirm.title}
                    message={confirm.message}
                    confirmLabel="Delete"
                    danger
                    onConfirm={() => { const fn = confirm.onConfirm; setConfirm(null); fn(); }}
                    onCancel={() => setConfirm(null)}
                />
            )}
        </>
    );
}
